<template>
  <div>
    <!-- 右下角悬浮按钮 -->
    <div class="publish-fab" @click="openPopup" :class="{ 'fab-animate': fabActive }">
      <van-icon name="photograph" color="#fff" size="20" />
    </div>
    <!-- 发布弹窗 -->
    <van-popup v-model="show" position="bottom" round :style="{ minHeight: '40vh' }" @open="onPopupOpen" @close="onPopupClose">
      <div class="publish-popup">
        <div class="popup-title">发布朋友圈</div>
        <van-field
          v-model="content"
          type="textarea"
          rows="3"
          autosize
          maxlength="2000"
          show-word-limit
          placeholder="这一刻的想法... #标签"
          class="publish-field"
        />
        <van-uploader
          v-model="mediaList"
          :max-count="9"
          :after-read="onAfterRead"
          accept="image/*,video/*"
          multiple
          class="publish-uploader"
        />
        <van-button
          type="primary"
          block
          round
          class="publish-btn"
          :disabled="!canPublish"
          :loading="isUploading"
          loading-text="照片/视频上传中..."
          @click="handlePublish"
        >
          发布
        </van-button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Uploader, Field, Button, Popup, Icon, Toast } from 'vant'
import { uploadToCos } from '@/utils/cosUploader'
import { publishMoment } from '@/api/orchard'

export default {
  name: 'OrchardPublish',
  components: { [Uploader.name]: Uploader, [Field.name]: Field, [Button.name]: Button, [Popup.name]: Popup, [Icon.name]: Icon },
  data () {
    return {
      show: false,
      content: '',
      mediaList: [], // {file, status, url, progress}
      fabActive: false,
      uploading: false
    }
  },
  computed: {
    canPublish () {
      // 至少有文本或已上传成功的媒体，且所有图片都上传完成
      const hasContent = this.content && this.content.trim().length > 0
      const hasMedia = this.mediaList.some(m => m.status === 'done')
      const allUploaded = this.mediaList.length === 0 || this.mediaList.every(m => m.status === 'done')
      return (hasContent || hasMedia) && allUploaded
    },
    isUploading () {
      // 只要有文件未上传完成就为true
      return this.mediaList.some(m => m.status !== 'done')
    }
  },
  methods: {
    openPopup () {
      this.fabActive = true
      setTimeout(() => { this.fabActive = false }, 200)
      this.show = true
    },
    // 🎭 弹窗打开时的处理
    onPopupOpen () {
      this.$root.$emit('show-popup') // 通知App隐藏导航栏
    },
    // 🎭 弹窗关闭时的处理
    onPopupClose () {
      this.$root.$emit('hide-popup') // 通知App显示导航栏
      // 原有的关闭逻辑
      this.content = ''
      this.mediaList = []
      this.uploading = false
    },
    getImages () {
      return this.mediaList.filter(m => m.status === 'done' && m.mediaType === 'image').map(m => m.url)
    },
    getVideos () {
      return this.mediaList.filter(m => m.status === 'done' && m.mediaType === 'video').map(m => m.url)
    },
    async onAfterRead (file) {
      const files = Array.isArray(file) ? file : [file]
      for (const f of files) {
        const type = f.type || (f.file && f.file.type) || ''
        const isImage = type.startsWith('image')
        const isVideo = type.startsWith('video')
        this.$set(f, 'mediaType', isImage ? 'image' : isVideo ? 'video' : 'other')
        if (f.size > 1024 * 1024 * 1024) {
          Toast.fail('单个文件不能超过1G')
          const idx = this.mediaList.indexOf(f)
          if (idx !== -1) this.mediaList.splice(idx, 1)
          continue
        }
        try {
          const url = await uploadToCos({
            file: f.file || f,
            onProgress: p => { this.$set(f, 'progress', Math.floor((p.percent || 0) * 100)) }
          })
          this.$set(f, 'url', url)
          this.$set(f, 'status', 'done')
        } catch (e) {
          const idx = this.mediaList.indexOf(f)
          if (idx !== -1) this.mediaList.splice(idx, 1)
          Toast.fail('上传失败，请检查网络或稍后重试')
        }
      }
    },
    async handlePublish () {
      if (!this.canPublish) {
        Toast('请填写内容或添加图片/视频')
        return
      }
      if (this.uploading) {
        Toast('请等待所有文件上传完成')
        return
      }
      const images = this.getImages()
      const videos = this.getVideos()
      try {
        await publishMoment({
          content: this.content.trim(),
          images,
          videos
        })
        this.show = false
        Toast.success('发布成功！')
        this.$emit('publish')
      } catch (e) {
        Toast.fail('发布失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.publish-fab {
  position: fixed;
  right: 24px;
  bottom: calc(env(safe-area-inset-bottom) + 6px); /* 与导航栏胶囊对齐 */
  width: 52px;  /* 与导航栏胶囊高度匹配 */
  height: 52px; /* 与导航栏胶囊高度匹配 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  overflow: hidden;
}

.publish-fab::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(255,255,255,0.3) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s;
}

.publish-fab:hover::before {
  opacity: 1;
}

.publish-fab:active,
.fab-animate {
  transform: scale(1.1);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
}

/* 移动端对齐导航栏 - 与MessageView断点保持一致 */
@media (max-width: 600px) {
  .publish-fab {
    bottom: calc(env(safe-area-inset-bottom) + 6px) !important; /* 与导航栏保持对齐 */
    right: 16px;
    width: 48px;  /* 与MessageView保持一致 */
    height: 48px; /* 与MessageView保持一致 */
  }
}

@media (max-width: 480px) {
  .publish-fab {
    bottom: calc(env(safe-area-inset-bottom) + 6px) !important; /* 与导航栏保持对齐 */
    right: 16px;
    width: 44px;  /* 与MessageView保持一致 */
    height: 44px; /* 与MessageView保持一致 */
  }
}

.publish-popup {
  padding: 24px 16px 32px 16px;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border-radius: 18px 18px 0 0;
  min-height: 40vh;
}

.popup-title {
  font-size: 1.18rem;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 16px;
  letter-spacing: 1px;
  text-align: center;
}

.publish-field {
  margin-bottom: 20px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  padding: 4px;
}

::v-deep .publish-field .van-field__control {
  background: transparent;
  color: #2d3748;
  line-height: 1.6;
}

::v-deep .publish-field:focus-within {
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.publish-uploader {
  margin-bottom: 20px;
}

::v-deep .publish-uploader .van-uploader__upload {
  background: rgba(255, 255, 255, 0.6);
  border: 2px dashed rgba(102, 126, 234, 0.3);
  border-radius: 8px;
}

::v-deep .publish-uploader .van-uploader__upload:hover {
  border-color: rgba(102, 126, 234, 0.5);
  background: rgba(102, 126, 234, 0.05);
}

.publish-btn {
  font-size: 1.12rem;
  font-weight: 600;
  letter-spacing: 1px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: #fff;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.publish-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.6s;
}

.publish-btn:hover::before {
  left: 100%;
}

.publish-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
}

.publish-btn:active {
  transform: translateY(0);
}

.publish-btn:disabled {
  background: linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%);
  box-shadow: 0 4px 12px rgba(203, 213, 224, 0.2);
  transform: none;
}

</style>
